import { message } from 'antd';
import { store } from 'store';
import { setIsLoggedIn, setUserLoading } from 'store/user/slice';

import { jwt } from './jwt';

/**
 * Tolerant error shape — accepts AxiosError, plain Error, or anything
 * `unknown` so call sites don't carry per-source casts. Implementation
 * inspects the runtime shape: axios responses first (the common path),
 * then plain Error, then string fallback.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ErrorLike = any;

const DEFAULT_MESSAGE = 'Something went wrong';

const extractMessage = (err: ErrorLike): string => {
  const body = err?.response?.data;

  if (body) {
    // BE 4xx shape: { error, message: string | string[], statusCode }
    if (Array.isArray(body.message)) {
      return body.message.filter(Boolean).join(', ') || DEFAULT_MESSAGE;
    }

    if (typeof body.message === 'string') {
      return body.message;
    }

    if (typeof body.error === 'string') {
      return body.error;
    }
  }

  if (typeof err?.message === 'string') {
    return err.message;
  }

  return DEFAULT_MESSAGE;
};

export const showErrorMessage = (err: ErrorLike, cb?: () => void) => {
  const status: number | undefined = err?.response?.status;

  if (status === 401) {
    void (async () => {
      const accessToken = await jwt.refreshToken();

      if (accessToken) {
        cb?.();
      } else {
        store.dispatch(setUserLoading(false));
        store.dispatch(setIsLoggedIn(false));
      }
    })();

    return;
  }

  message.error(extractMessage(err));
};

export const getFirstErrorMessage = (err: ErrorLike): string =>
  extractMessage(err);
